import { useContext, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import groupStyles from './CompositionAdvancedOptions.module.css';
import styles from './ScopeSelector.module.css';
import dropdownStyles from 'components/shared/modal/ModalDropdown.module.css';
import { ArrowRightTriangleIcon } from 'assets/icons';
import { PlusIcon } from 'features/compositions/assets/icons/icons';
import GuiContext from 'config/context/guiContext';
import { useCompositionsContext } from 'features/compositions/context/CompositionsContext';
import useTheme from 'config/useTheme';

const ScopeSelector = () => {
    const { selected: { selectedAccount } } = useContext(GuiContext);
    const { selectedComposition, selectScope } = useCompositionsContext();
    const { dark } = useTheme();

    const [showCustomInput, setShowCustomInput] = useState(false);
    const [customScope, setCustomScope] = useState('');
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    const scopeOptions = useMemo(() => {
        const dynamicScopes = (selectedComposition?.scopes?.map(s => s.scope) || []);
        const ordered = ['Add custom...', selectedAccount, ...dynamicScopes].filter(Boolean);
        return Array.from(new Set(ordered));
    }, [selectedComposition?.scopes, selectedAccount]);

    const handleSelect = useCallback((val) => {
        if (val === 'Add custom...') {
            setShowCustomInput(true);
            setOpen(false);
            return;
        }
        setShowCustomInput(false);
        setCustomScope('');
        selectScope(val);
        setOpen(false);
    }, [selectScope]);

    const handleAddCustom = useCallback(() => {
        const value = (customScope || '').trim();
        if (!value) return;
        selectScope(value);
        setShowCustomInput(false);
        setCustomScope('');
    }, [customScope, selectScope]);

    // Close on outside click
    useEffect(() => {
        const onClick = (e) => {
            if (!dropdownRef.current) return;
            if (!dropdownRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    return (
        <div className={groupStyles.select_option_group}>
            <div className={groupStyles.label}>
                Scope <span>({selectedComposition?.scopes?.length || 0})</span>
            </div>
            <div className={`${groupStyles.select_option_row} ${styles.row}`} ref={dropdownRef}>
                <div className={`${dropdownStyles.modal_dropdown} ${open ? dropdownStyles.opened : ''} ${dark ? dropdownStyles.dark : ''}`}>
                    <button onClick={() => { if (showCustomInput) { setShowCustomInput(false); setOpen(true); } else { setOpen(!open); } }}>
                        {showCustomInput ? (
                            <input
                                value={customScope}
                                onChange={(e) => setCustomScope(e.target.value)}
                                placeholder="Enter custom scope"
                                className={styles.inline_input}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') { setShowCustomInput(false); setCustomScope(''); setOpen(false); }
                                    if (e.key === 'Enter') { e.preventDefault(); handleAddCustom(); }
                                }}
                                autoFocus
                            />
                        ) : (
                            <span>{selectedComposition?.selected_scope || selectedAccount || 'Select Scope'}</span>
                        )}
                        <ArrowRightTriangleIcon />
                    </button>
                    {open && (
                        <div className={dropdownStyles.modal_dropdown_options}>
                            {scopeOptions.map((opt) => (
                                <button key={opt} onClick={() => handleSelect(opt)}>
                                    <span>{opt}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {/* input is inline inside the button when showCustomInput */}
                <button className={groupStyles.add_btn} onClick={handleAddCustom} disabled={showCustomInput && !customScope.trim()}><PlusIcon /></button>
            </div>
        </div>
    );
};

export default ScopeSelector;


